/**
 * Hook for auto-loading a session trail when sessionUuid is provided in mapConfig.
 * Used when opening the map from the post-session dialog to show the session's trail.
 */

import { useAppSelector } from '@repo/store/maps/hooks';
import { selectMapConfig } from '@repo/store/maps/slices/mapData';
import type { MapDataConfig } from '@repo/types/maps/map-types';
import { type RawSessionEvent, transformApiSession } from '@repo/types/sessions/session-types';
import { getPublicSession, getUserSession } from '@repo/wemod-api-client/sessions-api-client';
import { useEffect, useRef, useState } from 'react';
import { useSessionHistory } from './useSessionHistory';
const SESSION_LOAD_RETRY_DELAYS_MS = [500, 1500, 3000] as const;
type AutoLoadSessionRequest = {
  type: 'authenticated';
  key: string;
  sessionUuid: string;
  startedAt: string;
  completedAt?: string;
  titleId: string;
} | {
  type: 'public';
  key: string;
  publicSessionId: string;
};
export function getAutoLoadSessionRequest(mapConfig: MapDataConfig | null | undefined, authenticatedSessionReady: boolean): AutoLoadSessionRequest | null {
  if (mapConfig?.publicSessionId) {
    return {
      type: 'public',
      key: `public:${mapConfig.publicSessionId}`,
      publicSessionId: mapConfig.publicSessionId
    };
  }
  if (!authenticatedSessionReady || !mapConfig?.sessionUuid || !mapConfig.sessionStartedAt || !mapConfig.titleId) {
    return null;
  }
  return {
    type: 'authenticated',
    key: `authenticated:${mapConfig.sessionUuid}:${mapConfig.sessionStartedAt}:${mapConfig.sessionCompletedAt ?? ''}:${mapConfig.titleId}`,
    sessionUuid: mapConfig.sessionUuid,
    startedAt: mapConfig.sessionStartedAt,
    completedAt: mapConfig.sessionCompletedAt,
    titleId: mapConfig.titleId
  };
}
export function useAutoLoadSession(authenticatedSessionReady: boolean) {
  const mapConfig = useAppSelector(selectMapConfig);
  const {
    setCurrentSession,
    setTimelineVisible
  } = useSessionHistory();
  const loadedSessionKeyRef = useRef<string | null>(null);
  const retryStateRef = useRef<{
    key: string | null;
    count: number;
  }>({
    key: null,
    count: 0
  });
  const [retryVersion, setRetryVersion] = useState(0);

  // biome-ignore lint/correctness/useExhaustiveDependencies: retryVersion re-runs the load after a delayed retry.
  useEffect(() => {
    const request = getAutoLoadSessionRequest(mapConfig, authenticatedSessionReady);
    if (!request || loadedSessionKeyRef.current === request.key) {
      return;
    }
    if (retryStateRef.current.key !== request.key) {
      retryStateRef.current = {
        key: request.key,
        count: 0
      };
    }
    let cancelled = false;
    let retryTimeout: ReturnType<typeof setTimeout> | null = null;
    const scheduleRetry = () => {
      if (cancelled || retryStateRef.current.count >= SESSION_LOAD_RETRY_DELAYS_MS.length) {
        return;
      }
      const delay = SESSION_LOAD_RETRY_DELAYS_MS[retryStateRef.current.count];
      retryStateRef.current.count++;
      retryTimeout = setTimeout(() => setRetryVersion(version => version + 1), delay);
    };
    const loadSession = async () => {
      try {
        let positions: Array<{
          ts: string;
          x: string;
          y: string;
          z: string;
        }>;
        let events: RawSessionEvent[] | undefined;
        let isShared = false;
        let startedAt: string;
        let endTime: string;
        let realSessionId: string | undefined;
        let titleId: number;
        if (request.type === 'public') {
          const publicData = await getPublicSession(request.publicSessionId);
          realSessionId = publicData.metadata.session.sessionId;
          titleId = publicData.metadata.session.titleId;
          if (!publicData?.data?.positions || publicData.data.positions.length === 0) {
            scheduleRetry();
            return;
          }
          positions = publicData.data.positions;
          events = publicData.data.events ?? undefined;
          const firstTs = parseFloat(positions[0]?.ts ?? '0') * 1000;
          const lastTs = parseFloat(positions[positions.length - 1]?.ts ?? '0') * 1000;
          startedAt = publicData.metadata.session.startTime ?? new Date(firstTs).toISOString();
          endTime = publicData.metadata.session.endTime ?? new Date(lastTs).toISOString();
        } else {
          endTime = request.completedAt || new Date().toISOString();
          const sessionData = await getUserSession(request.sessionUuid, request.startedAt, endTime, request.titleId);
          if (!sessionData?.positions || sessionData.positions.length === 0) {
            scheduleRetry();
            return;
          }
          positions = sessionData.positions;
          events = sessionData.events;
          isShared = sessionData.isShared;
          startedAt = request.startedAt;
          titleId = Number(request.titleId);
        }
        const session = transformApiSession({
          uuid: realSessionId ?? (request.type === 'public' ? request.publicSessionId : request.sessionUuid),
          gameId: 0,
          titleId,
          titleSlug: null,
          titleName: null,
          name: null,
          status: 'completed',
          startedAt,
          completedAt: endTime,
          createdAt: startedAt,
          updatedAt: endTime
        }, {
          positions,
          events
        }, {
          isShared
        });
        if (cancelled) {
          return;
        }
        if (session.trail && session.trail.length > 0) {
          loadedSessionKeyRef.current = request.key;
          retryStateRef.current = {
            key: request.key,
            count: 0
          };
          setCurrentSession(session);
          setTimelineVisible(true);
        } else {
          scheduleRetry();
        }
      } catch (error) {
        console.warn('Failed to auto-load session:', error);
        scheduleRetry();
      }
    };
    void loadSession();
    return () => {
      cancelled = true;
      if (retryTimeout) {
        clearTimeout(retryTimeout);
      }
    };
  }, [authenticatedSessionReady, mapConfig, retryVersion, setCurrentSession, setTimelineVisible]);
}